import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { type AuthAccountType, getAuthPath } from "@/lib/auth-roles";
import { SignInForm } from "./components/form";

type SignInProps = {
  accountType?: AuthAccountType;
};

export default function SignIn({ accountType = "restaurant" }: SignInProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const isConsumer = accountType === "consumer";
  const title = isConsumer
    ? t("auth.consumer.signIn.title", "Entrar como consumidor")
    : t("auth.restaurant.signIn.title", "Entrar como restaurante");
  const description = isConsumer
    ? t("auth.consumer.signIn.description", "Ingresá para pedir delivery y guardar tus domicilios")
    : t("auth.restaurant.signIn.description", "Ingresá para gestionar tu restaurante");
  const signUpPath = getAuthPath(accountType, "sign-up", next);

  return (
    <>
      <div className="space-y-1 text-center">
        <h1 className="font-semibold text-2xl tracking-tight">{title}</h1>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>

      <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <SignInForm accountType={accountType} />
        </CardContent>
        <CardFooter className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-muted-foreground text-sm">
            <span className="mr-1 hidden sm:inline-block">{t("auth.signIn.noAccount")}</span>
            <Button aria-label="Sign up" asChild className="h-auto p-0 text-primary" variant="link">
              <Link to={signUpPath}>{t("auth.signIn.signUp")}</Link>
            </Button>
          </div>
          <Button
            aria-label="Reset password"
            asChild
            className="h-auto p-0 text-primary"
            variant="link"
          >
            <Link to="/auth/reset-password">{t("auth.signIn.forgotPassword")}</Link>
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}
